import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Flame,
  Globe2,
  Heart,
  MapPin,
  Music,
  Sparkles,
  Users,
  Zap,
} from "lucide-react";
import SEO from "../components/SEO";
import WhatsAppButton from "../components/WhatsAppButton";
import { PAGE_SEO } from "../data/seoData";
import { SEO_IMAGES } from "../data/seoImages";
import { BRAND_NAME } from "../data/contactInfo";
import { publicImage, FUSION_IMAGES } from "../data/pageImages";
import {
  fusionsIntro,
  fusionStyles,
  fusionHighlights,
  fusionFaqs,
} from "../data/fusionsData";
import "../styles/Fusions.css";

const CONTACT_SEARCH = { pathname: "/contact", search: "?type=wedding&form=open" };

const HIGHLIGHT_ICONS = [Flame, Sparkles, Heart, Globe2];

const STYLE_ICONS = {
  "carnatic-fusion": Music,
  "parai-folk": Zap,
  "panchavadyam-blend": Flame,
  "south-india-wedding": Heart,
  "contemporary-stage": Sparkles,
  "online-workshop": Users,
};

const Fusions = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = fusionStyles[activeIndex];
  const ActiveIcon = STYLE_ICONS[active.id] || Music;

  return (
    <main className="blend-page">
      <SEO
        title={PAGE_SEO.fusions.title}
        description={PAGE_SEO.fusions.description}
        path={PAGE_SEO.fusions.path}
        ogImage={SEO_IMAGES.fusions}
      />

      <header className="blend-hero">
        <img
          className="blend-hero__media"
          src={publicImage(FUSION_IMAGES.hero)}
          alt="Praana Beats — Chenda Melam fusion ensemble"
          width={1024}
          height={512}
          fetchPriority="high"
        />
        <div className="blend-hero__veil" aria-hidden="true" />
        <div className="blend-hero__content">
          <p className="blend-hero__brand">{BRAND_NAME}</p>
          <h1 className="blend-hero__title">{fusionsIntro.title}</h1>
          <p className="blend-hero__lead">{fusionsIntro.subtitle}</p>
          <div className="blend-hero__actions">
            <WhatsAppButton>Book Fusion Melam</WhatsAppButton>
            <Link to="/chenda-melam-chennai" className="blend-btn blend-btn--ghost">
              Wedding Melam
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </header>

      <section className="blend-section blend-intro" aria-labelledby="blend-intro-heading">
        <div className="blend-section__inner blend-section__inner--narrow">
          <p className="blend-kicker">Approach</p>
          <h2 id="blend-intro-heading" className="blend-heading">
            Fusion that keeps Melam identity
          </h2>
          <p className="blend-lead">{fusionsIntro.note}</p>
        </div>
      </section>

      <section className="blend-section blend-praana" aria-labelledby="praana-beats-heading">
        <div className="blend-praana__inner">
          <div className="blend-praana__copy">
            <p className="blend-kicker">Featured ensemble</p>
            <h2 id="praana-beats-heading" className="blend-heading blend-praana__title">
              Praana Beats
            </h2>
            <p className="blend-lead">
              Our signature fusion lineup — Kerala Chenda Melam with violin, flute, keys, and
              contemporary stage presence. Built for weddings, cultural nights, and high-energy live shows.
            </p>
            <WhatsAppButton>Book Praana Beats</WhatsAppButton>
          </div>
          <figure className="blend-praana__figure">
            <img
              src={publicImage(FUSION_IMAGES.praana)}
              alt="Praana Beats — Chenda Melam fusion ensemble with traditional and contemporary instruments"
              width={1024}
              height={512}
              loading="lazy"
            />
          </figure>
        </div>
      </section>

      <section className="blend-section blend-why" aria-label="Why book fusion Melam">
        <div className="blend-section__inner">
          <p className="blend-kicker">Why book us</p>
          <h2 className="blend-heading">Built for real celebrations</h2>
          <ul className="blend-why__list">
            {fusionHighlights.map((item, index) => {
              const Icon = HIGHLIGHT_ICONS[index] || Check;
              return (
                <li className="blend-why__item" key={item.title}>
                  <span className="blend-icon" aria-hidden="true">
                    <Icon size={22} strokeWidth={1.75} />
                  </span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section className="blend-section blend-styles" aria-labelledby="blend-styles-heading">
        <div className="blend-section__inner">
          <p className="blend-kicker">Formats</p>
          <h2 id="blend-styles-heading" className="blend-heading">
            Fusion styles we offer
          </h2>
          <p className="blend-lead">
            Choose a ready format or tell us your mix — we build Chenda Melam fusion sets for Chennai
            weddings, temple stages, and cultural collaborations.
          </p>

          <div className="blend-styles__layout">
            <ul className="blend-rail" role="listbox" aria-label="Fusion styles">
              {fusionStyles.map((style, index) => (
                <li key={style.id} role="option" aria-selected={index === activeIndex}>
                  <button
                    type="button"
                    className={`blend-rail__tab${index === activeIndex ? " is-active" : ""}`}
                    onClick={() => setActiveIndex(index)}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    <span className="blend-rail__index" aria-hidden="true">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="blend-rail__copy">
                      <span className="blend-rail__name">{style.title}</span>
                      <span className="blend-rail__meta">{style.tag}</span>
                    </span>
                    <span className="blend-rail__bar" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>

            <article className="blend-panel" id={active.id} key={active.id} aria-live="polite">
              <span className="blend-panel__tag">
                <ActiveIcon size={14} aria-hidden="true" />
                {active.tag}
              </span>
              <h3 className="blend-panel__title">{active.title}</h3>
              <p className="blend-panel__summary">{active.summary}</p>
              <p className="blend-panel__details">{active.details}</p>

              <div className="blend-panel__block">
                <h4>Instruments</h4>
                <div className="blend-panel__tags">
                  {active.instruments.map((inst) => (
                    <span key={inst}>{inst}</span>
                  ))}
                </div>
              </div>

              <div className="blend-panel__block">
                <h4>Best for</h4>
                <ul>
                  {active.occasions.map((o) => (
                    <li key={o}>
                      <Check size={14} aria-hidden="true" />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="blend-section blend-explore" aria-labelledby="blend-explore-heading">
        <div className="blend-section__inner">
          <p className="blend-kicker">Explore</p>
          <h2 id="blend-explore-heading" className="blend-heading">
            Related pages
          </h2>
          <nav className="blend-explore__links" aria-label="Related pages">
            <Link to="/instruments">Kerala Instruments <ArrowRight size={14} aria-hidden="true" /></Link>
            <Link to="/melams">Melam Forms <ArrowRight size={14} aria-hidden="true" /></Link>
            <Link to="/programs">All Programs <ArrowRight size={14} aria-hidden="true" /></Link>
            <Link to="/gallery">Gallery <ArrowRight size={14} aria-hidden="true" /></Link>
            <Link to="/chenda-classes-chennai">Learn Melam <ArrowRight size={14} aria-hidden="true" /></Link>
          </nav>
        </div>
      </section>

      <section className="blend-section blend-faq" aria-labelledby="blend-faq-heading">
        <div className="blend-section__inner blend-section__inner--narrow">
          <p className="blend-kicker">FAQ</p>
          <h2 id="blend-faq-heading" className="blend-heading">
            Fusion Melam FAQs
          </h2>
          <div className="blend-faq__list">
            {fusionFaqs.map((item) => (
              <details className="blend-faq__item" key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="blend-cta-band" aria-labelledby="blend-cta-heading">
        <div className="blend-cta-band__inner">
          <h2 id="blend-cta-heading">Ready to book Chenda Melam fusion?</h2>
          <p>
            Share your date, venue, and preferred instruments — we will suggest a fusion lineup and quote.
          </p>
          <div className="blend-hero__actions">
            <WhatsAppButton>WhatsApp Now</WhatsAppButton>
            <Link to={CONTACT_SEARCH} className="blend-btn blend-btn--ghost">
              <MapPin size={18} aria-hidden="true" />
              Request a Quote
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Fusions;
